/**
 * The value-state chip every dossier figure carries (C05): Derived with its
 * formula on hover, Suggested with its basis, Confirmed with who and when. A
 * stated figure's chip is whose statement it is (AttributionBadge), so the
 * state chip draws nothing for it.
 *
 * `lineageSource` is the one reading of a catalogue field's lineage row into
 * the chip's source, shared by the Overview, the Property tab's slots and the
 * Parties roster, so no tab invents its own.
 */
import { FIELD_STATE_TOKENS } from '@/components/fields/fieldState'
import { stateOfLineage } from '@/components/fields/lineage'
import type { LoanFieldLineageField } from '@/pages/pipeline-intelligence/types'
import type { FieldSourceRef } from './workspace/types'

/** A derived, suggested or confirmed figure says so on the row — with the
 *  formula on hover — so a number the platform worked out never reads as a
 *  statement (OVR-05). A stated figure's chip is whose statement it is. A
 *  blank an unread model or attachment may fill is also marked. */
export function ValueStateChip({ source }: { source?: FieldSourceRef | null }) {
  const state = source?.state
  if (!state || state === 'stated' || state === 'missing' || state === 'not_applicable') return null
  const token = FIELD_STATE_TOKENS[state]
  return (
    <span
      data-value-state={state}
      title={state === 'derived' && source?.formula ? `Derived: ${source.formula}` : source?.label ?? token.description}
      className={`rounded px-1 py-0.5 text-[7.5px] uppercase tracking-wide ring-1 ring-inset ${token.className}`}
    >
      {token.label}
    </span>
  )
}

/**
 * Whose word a covenant term is (Chris Young, 26 Oct 2026: "Covenant terms are
 * ours but start with sponsor provided initial ingestion until we change it").
 * Sponsor-provided, initial while the desk has not set its own, flagged as
 * awaiting the desk's terms; Desk-set once a person at the desk has. Drawn only
 * for the fields whose lineage carries an ownership; nothing otherwise.
 */
export function OwnershipMark({ source, className = '' }: { source?: FieldSourceRef | null; className?: string }) {
  const ownership = source?.ownership
  if (!ownership?.stamp) return null
  const sponsor = ownership.seed === 'sponsor'
  return (
    <span
      data-covenant-owner={ownership.seed ?? ''}
      data-awaiting-desk-terms={ownership.awaitingDeskTerms ? 'true' : undefined}
      title={ownership.note ? `${ownership.stamp}. ${ownership.note}` : ownership.stamp}
      className={`inline-block rounded px-1 py-px text-[7.5px] uppercase leading-tight tracking-wide ring-1 ring-inset ${
        sponsor ? 'bg-sky-400/10 text-sky-200 ring-sky-400/25' : 'bg-amber-400/10 text-amber-200 ring-amber-400/25'
      } ${className}`}
    >
      {sponsor ? 'Sponsor · initial' : 'Desk-set'}
    </span>
  )
}

/** The chip source of one catalogue field, from its lineage row: its agreed
 *  state, the one-line summary the lineage writes for it (the basis of a
 *  suggestion, the formula of a derivation, who confirmed it) and a derived
 *  field's formula. Null when the deal holds no figure for the field. */
export function lineageSource(
  fieldName: string,
  row: LoanFieldLineageField | null | undefined,
): FieldSourceRef | null {
  if (!row) return null
  const state = stateOfLineage(row)
  if (
    state !== 'stated'
    && state !== 'derived'
    && state !== 'suggested'
    && state !== 'confirmed'
    && state !== 'in_unread_model'
    && state !== 'in_unread_attachment'
  ) {
    return null
  }
  const formula = row.derivation?.formula ?? null
  const label =
    row.summary ||
    (state === 'derived' && formula ? `Derived: ${formula}` : null) ||
    row.source ||
    FIELD_STATE_TOKENS[state].description
  return {
    label,
    detail: row.reason ?? null,
    fieldName,
    // Whose figure: a stated one is whose statement the lineage names; a
    // confirmed one is the firm's; a derived or suggested one is nobody's.
    attribution: state === 'stated' ? row.provenance?.attribution ?? null : state === 'confirmed' ? 'client' : null,
    cellRef: row.provenance?.cellRef ?? null,
    disputed: false,
    state,
    formula,
    ownership: row.ownership ?? null,
  }
}
